import React, { useState, useEffect } from 'react';
import { Users, MessageSquare, BarChart3, Plus, Edit, Trash2, Save, X, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import TemplateManager from './TemplateManager';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'staff';
  whatsapp_phone_id?: string;
  whatsapp_app_id?: string;
  whatsapp_app_token?: string;
  whatsapp_permanent_token?: string;
  created_at: string;
  status: 'active' | 'inactive';
}

interface WhatsAppConfig {
  phone_id: string;
  app_id: string;
  app_token: string;
  permanent_token: string;
}

const AdminPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<'users' | 'whatsapp' | 'analytics' | 'templates'>('templates');
  const [users, setUsers] = useState<User[]>([]);
  const [whatsappConfig, setWhatsappConfig] = useState<WhatsAppConfig>({
    phone_id: '',
    app_id: '',
    app_token: '',
    permanent_token: ''
  });
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const { user: currentUser } = useAuth();

  // Mock data and auto-configure WhatsApp credentials
  useEffect(() => {
    const mockUsers: User[] = [
      {
        id: '1',
        email: 'admin@example.com',
        name: 'Super Admin',
        role: 'super_admin',
        created_at: '2024-01-01T00:00:00Z',
        status: 'active'
      },
      {
        id: '2',
        email: 'staff@example.com',
        name: 'Staff Member',
        role: 'staff',
        created_at: '2024-01-02T00:00:00Z',
        status: 'active'
      }
    ];
    setUsers(mockUsers);

    // Auto-configure WhatsApp credentials for demo
    const autoConfigureWhatsApp = () => {
      const demoConfig = {
        phone_id: '629216046949822',
        app_id: '633672219640106',
        permanent_token: 'EAAPqgrWwEfEBO8lz9W7MdOM1NYjsQPzZAlfduKvBwKarGsVCc9qV4ujKplHtwpdCfRQ822RDuSJWuX3aZBoStRLtb94ecJSDQiaZAPrKTCcZBGWvvp5BUAZB3j47SUsBwD5Jm5DnK7kRwE23yiY0X2rrUvI8TXhWE2W3HLwWDZCGNmkAjbxv2Qw8XmVq15ILtzEwZDZD',
        app_token: ''
      };
      
      // Check if already configured
      const existingConfig = JSON.parse(localStorage.getItem('whatsapp_config') || '{}');
      if (!existingConfig.permanent_token) {
        localStorage.setItem('whatsapp_config', JSON.stringify(demoConfig));
        setWhatsappConfig(demoConfig);
        console.log('🚀 Auto-configured WhatsApp Business API credentials for demo');
        toast.success('WhatsApp Business API credentials configured automatically!');
      } else {
        setWhatsappConfig(existingConfig);
      }
    };
    
    autoConfigureWhatsApp();
  }, []);

  const handleSaveWhatsAppConfig = () => {
    // Validate required fields
    if (!whatsappConfig.app_id || !whatsappConfig.permanent_token || !whatsappConfig.phone_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Save to localStorage for now (in production, this should be saved securely on the backend)
    localStorage.setItem('whatsapp_config', JSON.stringify(whatsappConfig));
    toast.success('WhatsApp configuration saved successfully');
  };

  const handleSaveUser = (userData: Partial<User>) => {
    if (editingUser) {
      // Update existing user
      setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...userData } : u));
      toast.success('User updated successfully');
    } else {
      // Create new user
      const newUser: User = {
        id: Date.now().toString(),
        created_at: new Date().toISOString(),
        status: 'active',
        ...userData
      } as User;
      setUsers([...users, newUser]);
      toast.success('User created successfully');
    }
    setEditingUser(null);
    setShowUserForm(false);
  };

  const handleDeleteUser = (userId: string) => {
    setUsers(users.filter(u => u.id !== userId));
    toast.success('User deleted successfully');
  };

  const UserForm: React.FC<{ user?: User; onSave: (data: Partial<User>) => void; onCancel: () => void }> = ({ 
    user, onSave, onCancel 
  }) => {
    const [formData, setFormData] = useState({
      name: user?.name || '',
      email: user?.email || '',
      role: user?.role || 'staff',
      whatsapp_phone_id: user?.whatsapp_phone_id || '',
      whatsapp_app_id: user?.whatsapp_app_id || '',
      whatsapp_app_token: user?.whatsapp_app_token || '',
      whatsapp_permanent_token: user?.whatsapp_permanent_token || ''
    });

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-96 overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{user ? 'Edit User' : 'Add User'}</h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={currentUser?.role !== 'super_admin'}
              >
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                {currentUser?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
              </select>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-md font-medium text-gray-700 mb-2">WhatsApp Configuration</h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Phone ID</label>
                  <input
                    type="text"
                    value={formData.whatsapp_phone_id}
                    onChange={(e) => setFormData({ ...formData, whatsapp_phone_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">App ID</label>
                  <input
                    type="text"
                    value={formData.whatsapp_app_id}
                    onChange={(e) => setFormData({ ...formData, whatsapp_app_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">App Token</label>
                  <input
                    type="password"
                    value={formData.whatsapp_app_token}
                    onChange={(e) => setFormData({ ...formData, whatsapp_app_token: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Permanent Token</label>
                  <input
                    type="password"
                    value={formData.whatsapp_permanent_token}
                    onChange={(e) => setFormData({ ...formData, whatsapp_permanent_token: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(formData)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Save size={16} className="inline mr-1" />
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="border-b p-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Admin Panel</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex space-x-8 px-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'templates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText size={16} className="inline mr-1" />
              Templates
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users size={16} className="inline mr-1" />
              Users
            </button>
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'whatsapp'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageSquare size={16} className="inline mr-1" />
              WhatsApp
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <BarChart3 size={16} className="inline mr-1" />
              Analytics
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'templates' && <TemplateManager />}
          
          {activeTab === 'users' && (
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">User Management</h3>
                <button
                  onClick={() => setShowUserForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
                >
                  <Plus size={16} className="mr-1" />
                  Add User
                </button>
              </div>

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <li key={user.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                              user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {user.role.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          {user.whatsapp_phone_id && (
                            <p className="text-xs text-gray-400">WhatsApp: {user.whatsapp_phone_id}</p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setEditingUser(user);
                              setShowUserForm(true);
                            }}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit size={16} />
                          </button>
                          {currentUser?.role === 'super_admin' && user.role !== 'super_admin' && (
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'whatsapp' && (
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">WhatsApp Business API Configuration</h3>
                <div className="text-sm text-gray-500">
                  Required for template submission and broadcasting
                </div>
              </div>
              
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Setup Instructions:</h4>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Create a WhatsApp Business Account at <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">business.facebook.com</a></li>
                  <li>Set up a WhatsApp Business API app in Meta Developer Console</li>
                  <li>Get your Business Phone Number ID from the WhatsApp API setup</li>
                  <li>Generate a permanent access token with whatsapp_business_messaging permissions</li>
                  <li>Add your webhook URL for receiving messages</li>
                </ol>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Phone Number ID *
                    </label>
                    <input
                      type="text"
                      value={whatsappConfig.phone_id}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phone_id: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 123456789012345"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Found in WhatsApp Business API → Phone Numbers section
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WhatsApp Business Account ID *
                    </label>
                    <input
                      type="text"
                      value={whatsappConfig.app_id}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, app_id: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 123456789012345"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your WhatsApp Business Account ID (not the App ID)
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      App Token (Optional)
                    </label>
                    <input
                      type="password"
                      value={whatsappConfig.app_token}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, app_token: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter temporary app token"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Temporary token for testing (expires in 24 hours)
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Permanent Access Token *
                    </label>
                    <input
                      type="password"
                      value={whatsappConfig.permanent_token}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, permanent_token: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter permanent access token"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      System User token with whatsapp_business_messaging permissions
                    </p>
                  </div>
                  
                  <div className="flex justify-between">
                    <button
                      onClick={async () => {
                        if (!whatsappConfig.app_id || !whatsappConfig.permanent_token) {
                          toast.error('Please enter App ID and Permanent Token first');
                          return;
                        }
                        
                        toast.loading('Testing connection...', { id: 'connection-test' });
                        
                        try {
                          // Test 1: Basic WhatsApp Business Account access
                          const response = await fetch(`https://graph.facebook.com/v18.0/${whatsappConfig.app_id}?access_token=${whatsappConfig.permanent_token}`);
                          const data = await response.json();
                          
                          if (!response.ok) {
                            throw new Error(data.error?.message || `HTTP ${response.status}: ${response.statusText}`);
                          }
                          
                          // Test 2: Try to fetch phone numbers to validate WhatsApp permissions
                          const phoneResponse = await fetch(`https://graph.facebook.com/v18.0/${whatsappConfig.app_id}/phone_numbers?access_token=${whatsappConfig.permanent_token}`);
                          const phoneData = await phoneResponse.json();
                          
                          if (phoneResponse.ok && phoneData.data) {
                            toast.success(`✅ Connection successful! Account: ${data.name || 'WhatsApp Business'} (${phoneData.data.length} phone numbers)`, { id: 'connection-test' });
                          } else if (phoneResponse.status === 403) {
                            toast.success(`✅ Basic connection successful! Account: ${data.name || 'WhatsApp Business'} (Limited permissions - may need phone number access)`, { id: 'connection-test' });
                          } else {
                            toast.success(`✅ Basic connection successful! Account: ${data.name || 'WhatsApp Business'}`, { id: 'connection-test' });
                          }
                        } catch (error: any) {
                          console.error('Connection test error:', error);
                          toast.error(`❌ Connection failed: ${error.message}`, { id: 'connection-test' });
                        }
                      }}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
                    >
                      <CheckCircle size={16} className="mr-1" />
                      Test Connection
                    </button>
                    
                    <button
                      onClick={handleSaveWhatsAppConfig}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 flex items-center"
                    >
                      <Save size={16} className="mr-1" />
                      Save Configuration
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="p-4">
              <h3 className="text-lg font-medium mb-4">Analytics Dashboard</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Users className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                          <dd className="text-lg font-medium text-gray-900">{users.length}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <MessageSquare className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">Active Conversations</dt>
                          <dd className="text-lg font-medium text-gray-900">0</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* User Form Modal */}
        {showUserForm && (
          <UserForm
            user={editingUser || undefined}
            onSave={handleSaveUser}
            onCancel={() => {
              setShowUserForm(false);
              setEditingUser(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPanel; 